import { useContext, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import type {
  DisposeLvaItem,
  LvaCondition,
  LvaItemListRow,
  MarkDamagedLvaItem,
  MarkLostLvaItem,
  ReassignCustodian,
  TransferLvaItem,
} from '@dcc-erp/shared';

import { AuthContext } from '../../lib/auth/useAuth';
import { useBranches, useEmployees } from '../../lib/queries';
import { DepartmentSelect } from '../dimensions';
import { SearchSelect } from '../SearchSelect';

// ============================================================
// The 5 LVA lifecycle action sub-modals. Each is presentational + controlled:
// it collects the typed body for one endpoint and calls onSubmit(data). The
// parent (LvaDetailModal) owns the mutation + closes on success. SoD is enforced
// server-side in the employee-id namespace; LostModal/DisposeModal collect
// reason + an approver and submit approvedBy = the picked employee's id (NOT the
// display string), so the server can compare it against the submitter's
// resolved employee id.
// ============================================================

const today = (): string => new Date().toISOString().slice(0, 10);

const labelStyle: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-muted)',
  fontWeight: 500,
  display: 'block',
  marginBottom: 6,
};

const inputStyle: CSSProperties = { width: '100%', height: 38, fontSize: 14 };

const LVA_CONDITION_LABEL: Readonly<Record<LvaCondition, string>> = {
  NEW: 'ใหม่',
  GOOD: 'ดี',
  FAIR: 'พอใช้',
  POOR: 'ทรุดโทรม',
  DAMAGED: 'ชำรุด',
};

// Shared modal shell — overlay + card + header + footer (cancel + a primary
// submit). Keeps each modal body small.
function ModalShell({
  title,
  busy,
  submitLabel,
  canSubmit,
  error,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  busy: boolean;
  submitLabel: string;
  canSubmit: boolean;
  // Server (or client) error to surface in a red box; the modal stays open so
  // the user can read it and retry. Mirrors the LvaFormModal error box pattern.
  // Widened with `undefined` for exactOptionalPropertyTypes (callers forward an
  // optional ActionModalProps.error).
  error?: string | null | undefined;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 110,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 460,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3
            style={{ margin: 0, fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            {title}
          </h3>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {children}
          {error && (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                background: '#fce9e7',
                borderRadius: 6,
                fontSize: 14,
                color: 'var(--negative)',
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" type="button" onClick={onClose} disabled={busy}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={onSubmit}
            disabled={busy || !canSubmit}
          >
            {busy ? 'กำลังบันทึก…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DateField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={labelStyle}>วันที่</label>
      <input
        className="input"
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    </div>
  );
}

function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={labelStyle}>เหตุผล</label>
      <textarea
        className="input"
        placeholder="เหตุผล / รายละเอียด"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', minHeight: 64, fontSize: 14, resize: 'vertical' }}
      />
    </div>
  );
}

// Approver picker — employee SearchSelect; submits the picked employee's ID as
// approvedBy (single-namespace SoD: the server compares this id against the
// submitter's resolved employee id, and resolves a readable label for the audit
// log). ariaLabel makes the combobox nameable.
//
// L5 (spec §7): the server resolves the SUBMITTER's employee by matching the
// signed-in user's email to Employee.email within the company, then rejects
// approvedBy === submitter (SoD). We mirror that mapping client-side as an early
// WARNING: if the picked approver's employee email equals the current user's
// email we flag it (server stays authoritative). We read the auth context
// directly (non-throwing useContext, not useAuth()) so the field still renders
// outside a provider / under exhaustive query mocks — when identity is not
// resolvable we simply skip the warning.
function ApproverField({
  companyId,
  value,
  onChange,
}: {
  companyId: string | undefined;
  value: string;
  onChange: (employeeId: string) => void;
}) {
  const employeesData = useEmployees(companyId).data;
  const employees = useMemo(() => employeesData ?? [], [employeesData]);
  const auth = useContext(AuthContext);
  const currentUserEmail = auth?.user?.email?.trim().toLowerCase() ?? '';
  const optionLabel = (id: string) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.code} — ${e.nameTh}` : '';
  };
  // Resolve the current user's own employee id by email (same rule the server
  // uses); null when not resolvable → no warning.
  const currentEmployeeId = useMemo(() => {
    if (!currentUserEmail) return null;
    return employees.find((e) => e.email?.trim().toLowerCase() === currentUserEmail)?.id ?? null;
  }, [employees, currentUserEmail]);
  const isSelf = !!value && currentEmployeeId != null && value === currentEmployeeId;
  // value IS the selected employee id (= approvedBy). The display label is
  // derived for the combobox only; we never submit the display string.
  return (
    <div>
      <label style={labelStyle}>ผู้อนุมัติ</label>
      <SearchSelect
        value={value}
        onChange={onChange}
        placeholder="— เลือกผู้อนุมัติ —"
        options={employees.map((e) => ({ value: e.id, label: `${e.code} — ${e.nameTh}` }))}
        getLabel={optionLabel}
        ariaLabel="ผู้อนุมัติ"
      />
      {isSelf && (
        <div role="alert" style={{ marginTop: 6, fontSize: 13, color: 'var(--warning, #b54708)' }}>
          ผู้อนุมัติต้องไม่ใช่ผู้บันทึกรายการ (คุณ) — ระบบจะปฏิเสธหากผู้อนุมัติเป็นบัญชีเดียวกัน
        </div>
      )}
    </div>
  );
}

interface ActionModalProps<T> {
  item: LvaItemListRow;
  busy: boolean;
  // Server/client error surfaced by the parent (lifted in LvaDetailModal). The
  // modal stays open so the user can read it and retry.
  error?: string | null;
  onClose: () => void;
  onSubmit: (data: T) => void;
}

export function TransferModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: ActionModalProps<TransferLvaItem>) {
  const companyId = useMemo(() => item.companyId, [item.companyId]);
  const branches = useBranches(companyId).data ?? [];
  const [date, setDate] = useState(today());
  const [toBranchId, setToBranchId] = useState('');
  const [toDepartmentId, setToDepartmentId] = useState('');
  const [reason, setReason] = useState('');
  // ≥1 dimension (branch OR department) must change — mirrors TransferLvaItemSchema's refine.
  const canSubmit = (!!toBranchId || !!toDepartmentId) && reason.trim().length > 0;
  return (
    <ModalShell
      title={`โอนย้าย ${item.code}`}
      busy={busy}
      submitLabel="ยืนยันโอนย้าย"
      canSubmit={canSubmit}
      error={error}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          date,
          ...(toBranchId ? { toBranchId } : {}),
          ...(toDepartmentId ? { toDepartmentId } : {}),
          reason,
        })
      }
    >
      <DateField value={date} onChange={setDate} />
      <div>
        <label style={labelStyle} htmlFor="lva-transfer-branch">
          สาขาปลายทาง
        </label>
        <select
          id="lva-transfer-branch"
          aria-label="สาขาปลายทาง"
          className="select"
          value={toBranchId}
          onChange={(e) => setToBranchId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— เลือกสาขา —</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} {b.nameTh}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={labelStyle} id="lva-transfer-dept-label">
          หน่วยงานปลายทาง
        </label>
        <DepartmentSelect
          companyId={companyId}
          value={toDepartmentId}
          onChange={setToDepartmentId}
          branchId={toBranchId}
          style={inputStyle}
        />
      </div>
      <ReasonField value={reason} onChange={setReason} />
    </ModalShell>
  );
}

export function ReassignModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: ActionModalProps<ReassignCustodian>) {
  const companyId = useMemo(() => item.companyId, [item.companyId]);
  const employees = useEmployees(companyId).data ?? [];
  const [date, setDate] = useState(today());
  const [empId, setEmpId] = useState('');
  const [reason, setReason] = useState('');
  const canSubmit = !!empId && reason.trim().length > 0;
  const optionLabel = (id: string) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.code} — ${e.nameTh}` : '';
  };
  return (
    <ModalShell
      title={`เปลี่ยนผู้ถือครอง ${item.code}`}
      busy={busy}
      submitLabel="ยืนยันเปลี่ยนผู้ถือครอง"
      canSubmit={canSubmit}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit({ date, toCustodianEmployeeId: empId, reason })}
    >
      <DateField value={date} onChange={setDate} />
      <div>
        <label style={labelStyle}>ผู้ถือครองใหม่</label>
        <SearchSelect
          value={empId}
          onChange={setEmpId}
          placeholder="— เลือกพนักงาน —"
          options={employees.map((e) => ({ value: e.id, label: `${e.code} — ${e.nameTh}` }))}
          getLabel={optionLabel}
          ariaLabel="ผู้ถือครองใหม่"
        />
      </div>
      <ReasonField value={reason} onChange={setReason} />
    </ModalShell>
  );
}

export function DamageModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: ActionModalProps<MarkDamagedLvaItem>) {
  const [date, setDate] = useState(today());
  const [condition, setCondition] = useState<LvaCondition>('DAMAGED');
  const [reason, setReason] = useState('');
  const canSubmit = reason.trim().length > 0;
  return (
    <ModalShell
      title={`แจ้งชำรุด ${item.code}`}
      busy={busy}
      submitLabel="ยืนยันแจ้งชำรุด"
      canSubmit={canSubmit}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit({ date, condition, reason })}
    >
      <DateField value={date} onChange={setDate} />
      <div>
        <label style={labelStyle} htmlFor="lva-damage-condition">
          สภาพ
        </label>
        <select
          id="lva-damage-condition"
          aria-label="สภาพ"
          className="select"
          value={condition}
          onChange={(e) => setCondition(e.target.value as LvaCondition)}
          style={inputStyle}
        >
          {(['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'] as const).map((cd) => (
            <option key={cd} value={cd}>
              {LVA_CONDITION_LABEL[cd]}
            </option>
          ))}
        </select>
      </div>
      <ReasonField value={reason} onChange={setReason} />
    </ModalShell>
  );
}

export function LostModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: ActionModalProps<MarkLostLvaItem>) {
  const companyId = useMemo(() => item.companyId, [item.companyId]);
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const canSubmit = reason.trim().length > 0 && approvedBy.trim().length > 0;
  return (
    <ModalShell
      title={`แจ้งสูญหาย ${item.code}`}
      busy={busy}
      submitLabel="ยืนยันแจ้งสูญหาย"
      canSubmit={canSubmit}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit({ date, reason, approvedBy })}
    >
      <DateField value={date} onChange={setDate} />
      <ReasonField value={reason} onChange={setReason} />
      <ApproverField companyId={companyId} value={approvedBy} onChange={setApprovedBy} />
    </ModalShell>
  );
}

export function DisposeModal({
  item,
  busy,
  error,
  onClose,
  onSubmit,
}: ActionModalProps<DisposeLvaItem>) {
  const companyId = useMemo(() => item.companyId, [item.companyId]);
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const canSubmit = reason.trim().length > 0 && approvedBy.trim().length > 0;
  return (
    <ModalShell
      title={`จำหน่าย / ขายซาก ${item.code}`}
      busy={busy}
      submitLabel="ยืนยันจำหน่าย"
      canSubmit={canSubmit}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit({ date, reason, approvedBy })}
    >
      <DateField value={date} onChange={setDate} />
      <ReasonField value={reason} onChange={setReason} />
      <ApproverField companyId={companyId} value={approvedBy} onChange={setApprovedBy} />
    </ModalShell>
  );
}
